import { Link } from 'react-router-dom';
import { BookOpen, GraduationCap, Users } from 'lucide-react';
import { revealStyle, useScrollReveal } from '@/hooks/useScrollReveal';
import { cn } from '@/lib/utils';

/** Doors (spec 010 §3.3): route by role. One wide card, two narrow, unequal on purpose. */
const DOORS = [
  {
    title: 'Students',
    body: 'Start with the free SAT course. Add a tutor, for the SAT or anything else, when you want one.',
    href: '/what-we-do/students/',
    cta: 'For students →',
    icon: GraduationCap,
    wide: true,
  },
  {
    title: 'Parents',
    body: 'See what your student is working on, week by week. Book a tutor when it helps.',
    href: '/what-we-do/parents/',
    cta: 'For parents →',
    icon: Users,
    wide: false,
  },
  {
    title: 'Tutors',
    body: 'Run your tutoring here: scheduling, sessions, payments, and students who arrive with a diagnostic done.',
    href: '/what-we-do/tutors/',
    cta: 'For tutors →',
    icon: BookOpen,
    wide: false,
  },
] as const;

export function DoorsSection() {
  const { ref, isVisible } = useScrollReveal(0.1);
  return (
    <section aria-labelledby="doors-title" className="mx-auto w-full max-w-6xl px-4 py-14 sm:px-6 lg:py-16">
      <h2 id="doors-title" className="text-2xl font-semibold tracking-tight sm:text-3xl">
        Who Mentara is for
      </h2>
      <div
        ref={ref}
        style={revealStyle(isVisible)}
        className="mt-7 grid gap-4 lg:grid-cols-[1.7fr_1fr_1fr]"
      >
        {DOORS.map(({ title, body, href, cta, icon: Icon, wide }) => (
          <Link
            key={title}
            to={href}
            data-testid={`door-${title.toLowerCase()}`}
            className={cn(
              'group flex flex-col justify-between rounded-2xl border p-6 transition-transform duration-200 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background motion-reduce:transition-none motion-reduce:hover:translate-y-0',
              wide
                ? 'bg-primary text-primary-foreground lg:min-h-[15rem] lg:p-8'
                : 'bg-card',
            )}
          >
            <div>
              <Icon className={cn('h-6 w-6', wide ? 'text-primary-foreground/80' : 'text-primary')} aria-hidden />
              <h3 className={cn('mt-4 font-semibold tracking-tight', wide ? 'text-3xl' : 'text-xl')}>{title}</h3>
              <p
                className={cn(
                  'mt-2 leading-relaxed',
                  wide ? 'max-w-[28rem] text-base text-primary-foreground/90 lg:text-lg' : 'text-sm text-muted-foreground',
                )}
              >
                {body}
              </p>
            </div>
            <span className={cn('mt-6 text-sm font-medium underline-offset-4 group-hover:underline', !wide && 'text-primary')}>
              {cta}
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}
